import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUserGroup, faHourglassHalf, faCheckCircle, faCheck, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { primaryColor } from "../../../../../utils/comon";
import MetricCard from "../../../../common/Card/MetricCard";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import apiServices from "../../../../../services/apiService";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import ListSkeleton from "../../../../common/Skeleton/ListSkeleton";

const TruncatedText = ({ text, maxLength = 20, className = "" }) => {
    if (!text || text === "—") return <span className={className}>{"—"}</span>;
    if (text.length <= maxLength) return <span className={className}>{text}</span>;
    return (
        <div className="relative group inline-block max-w-[150px] sm:max-w-[200px] align-middle">
            <span className={`block truncate text-ellipsis overflow-hidden whitespace-nowrap ${className}`}>{text}</span>
            <div className="absolute left-1/2 bottom-full -translate-x-1/2 mb-2 z-[999] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                <div className="bg-[#1E293B] text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap">
                    {text}
                </div>
                <div className="w-2 h-2 bg-[#1E293B] rotate-45 mx-auto -mt-1"></div>
            </div>
        </div>
    );
};

const WalkInVisitorList = () => {
    const { user } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("Status");
    const [dateFilter, setDateFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [visitors, setVisitors] = useState([]);

    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const formatEntryTime = (isoString) => {
        if (!isoString) return "—";
        const d = new Date(isoString);
        if (isNaN(d.getTime())) return "—";
        const dateStr = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
        const timeStr = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
        return `${dateStr} ${timeStr}`;
    };

    const getWaitingTime = (dateStr) => {
        if (!dateStr) return "10m ago";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "10m ago";
        const diff = Math.max(0, Math.floor((new Date() - d) / 60000));
        if (diff < 60) return `${diff}m ago`;
        const hours = Math.floor(diff / 60);
        return `${hours}h ${diff % 60}m ago`;
    };

    const statusOptions = [
        { label: "All Status", value: "Status" },
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
        { label: "Pending", value: "PENDING" },
        { label: "Approved", value: "APPROVED" },
        { label: "Pass Issued", value: "PASS_ISSUED" },
        { label: "Checked-In", value: "CHECKED-IN" },
        { label: "Checked-Out", value: "CHECKED-OUT" },
        { label: "Rejected", value: "REJECTED" }
    ];

    const siteId = user?.siteId;
    const unitId = user?.branchorunitID;
    const roleName = (user?.RoleName || user?.roleName || user?.role || user?.Role || localStorage.getItem('roleName') || "").toLowerCase();
    const isSiteAdmin = roleName.includes('site admin') || roleName.includes('super');

    const fetchWalkInVisitors = async () => {
        try {
            setLoading(true);
            let url = `/Visitor/getAllVisitor?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}&visitorTypeId=2`;
            if (siteId) url += `&siteId=${siteId}`;
            if (!isSiteAdmin && unitId) {
                url += `&unitId=${unitId}`;
            }
            if (searchQuery) {
                url += `&searchText=${encodeURIComponent(searchQuery)}`;
            }

            const response = await apiServices({
                method: "GET",
                url: url,
            });
            if (response?.success) {
                const apiData = response?.data?.data || [];
                const fetchedList = Array.isArray(apiData) ? apiData : [];

                fetchedList.sort((a, b) => {
                    const dateA = new Date(a.createdDate || a.requestedOn || a.expectedEntryTime || 0).getTime();
                    const dateB = new Date(b.createdDate || b.requestedOn || b.expectedEntryTime || 0).getTime();
                    return dateB - dateA;
                });
                setVisitors(fetchedList);

                const total = response?.data?.totalCount || response?.data?.totalRecords || response?.data?.total || response?.totalRecords || fetchedList.length;
                setPagination(prev => ({ ...prev, totalRecords: total }));
            }
        } catch (error) {
            console.error("Error fetching walk-in visitors:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWalkInVisitors();
    }, [pagination.pageNumber, pagination.pageSize, searchQuery]);

    const handleUpdateStatus = async (row, statusId) => {
        try {
            setLoading(true);
            const payload = {
                statusId: statusId,
                unitId: row?.unitid || row?.unitId
            };
            const response = await apiServices({
                method: "PUT",
                url: `/Visitor/updateVisitorStatus/${row?.mobile || row?.visitorMobileNumber}`,
                data: payload,
            });
            if (response?.success || response?.isSuccess) {
                toast.success(`Visitor ${statusId === 1 ? "approved" : statusId === 3 ? "rejected" : statusId === 6 ? "blocked" : "updated"} successfully`);
                fetchWalkInVisitors();
            } else {
                toast.error(`Failed to update visitor status`);
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const handleCheckOut = async (phoneNumber) => {
        try {
            const roleId = Number(user?.roleId || 0);
            const checkoutRoleId = roleId > 0
                ? roleId
                : (isSiteAdmin ? 3 : (roleName.includes("tenant") ? 6 : 4));
            const payload = { roleId: checkoutRoleId };
            if (checkoutRoleId === 4 || checkoutRoleId === 6) {
                const checkoutUnitId = Number(unitId || 0);
                if (!checkoutUnitId) {
                    toast.error("This account has no unit, so checkout was not sent.");
                    return;
                }
                payload.unitId = checkoutUnitId;
            }
            const response = await apiServices({
                method: "PUT",
                url: `/Visitor/VisitorCheckOut/${phoneNumber}`,
                data: payload,
            });
            const body = response?.data || {};
            const ok = response?.success && (body.isSuccess === true || body.IsSuccess === true);
            if (ok) {
                toast.success(body.message || "Visitor checked out successfully");
                fetchWalkInVisitors();
            } else {
                toast.error(body.message || "Failed to checkout visitor");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to checkout visitor");
        }
    };

    const filteredVisitors = visitors.filter((v) => {
        const currentStatus = (v?.status || "").toUpperCase();
        const matchesStatus = statusFilter === "Status" || currentStatus === statusFilter.toUpperCase();
        const matchesDate = !dateFilter || (v?.requestedOn && v.requestedOn.split("T")[0] === dateFilter);

        return matchesStatus && matchesDate;
    });

    const totalRequests = visitors.length;
    const pendingRequests = visitors.filter(v => (v.status || "").toUpperCase() === "PENDING").length;
    const approvedToday = visitors.filter(v => {
        const s = (v.status || "").toUpperCase();
        return s === "PASS_ISSUED" || s === "APPROVED" || s === "CHECKED-IN";
    }).length;

    const getStatusStyles = (status) => {
        const normalizedStatus = (status || "").toUpperCase().replace(/[-_ ]/g, "");
        switch (normalizedStatus) {
            case "PASSISSUED": return "bg-[#DCFCE7] text-[#166534]";
            case "CHECKEDIN": return "bg-[#004B591A] text-[#004B59]";
            case "CHECKEDOUT":
            case "CHECKOUT": return "bg-[#E31F262B] text-[#E31F26]";
            case "PENDING": return "bg-[#FEF9C3] text-[#D97706]";
            case "APPROVED": return "bg-[#DCFCE7] text-[#166534]";
            case "REJECTED": return "bg-[#E31F26] text-[#FFFFFF]";
            case "BLOCKED":
            case "BLOCK": return "bg-[#0F172A] text-white border border-[#0F172A]";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    const tableActions = (
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 w-full sm:w-64">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </span>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPagination(prev => ({ ...prev, pageNumber: 1 }));
                    }}
                    placeholder="Search visitors..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-bordercolor rounded-lg text-sm focus:outline-none bg-transparent text-textprimary hover:border-gray-400"
                />
            </div>
            <div className="w-full sm:w-40 relative">
                <CustomSelect
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === statusFilter)}
                    onChange={(opt) => setStatusFilter(opt.value)}
                    placeholder="Status"
                />
            </div>
            <div className="relative flex items-center bg-[#F8FAFC] dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg px-3 py-2 text-sm font-medium text-[#475569] dark:text-textprimary w-full sm:w-auto hover:border-gray-400">
                <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="bg-transparent border-none outline-none font-medium text-[#475569] dark:text-textprimary cursor-pointer w-full"
                />
            </div>
        </div>
    );

    const displayedVisitors = useMemo(() => {
        return filteredVisitors;
    }, [filteredVisitors]);

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 gap-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading="Walk-In Visitor List"
                subHeading="Manage all Walk-In Visitors"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard title="TOTAL WALK-INS" value={String(totalRequests).padStart(2, "0")} icon={faUserGroup} />
                <MetricCard title="PENDING" value={String(pendingRequests).padStart(2, "0")} icon={faHourglassHalf} />
                <MetricCard title="APPROVED" value={String(approvedToday).padStart(2, "0")} icon={faCheckCircle} />
            </div>

            {/* Filter actions panel */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-sectionheaderbg p-4 border border-slate-100 dark:border-bordercolor rounded-2xl shadow-sm">
                <h3 className="font-bold text-sm text-[#0F172A] dark:text-textprimary">Visitors</h3>
                {tableActions}
            </div>

            <div className="shrink-0 flex-1 flex flex-col gap-4">
                {/* Custom Card List */}
                <div className="space-y-4">
                    {loading ? (
                        <ListSkeleton count={4} />
                    ) : filteredVisitors.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4 border border-dashed border-slate-200 dark:border-bordercolor rounded-2xl bg-white dark:bg-sectionheaderbg text-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-actionitembg border border-slate-200 dark:border-bordercolor flex items-center justify-center text-slate-400 dark:text-textsecondary">
                                <FontAwesomeIcon icon={faUser} className="text-lg" />
                            </div>
                            <div className="flex flex-col gap-1">
                                <h5 className="font-bold text-slate-700 dark:text-textprimary text-sm">No Visitors Found</h5>
                                <p className="text-xs text-slate-400 dark:text-textsecondary max-w-[280px]">We couldn't find any walk-in visitors matching your search or filters.</p>
                            </div>
                        </div>
                    ) : displayedVisitors.map((row, index) => {
                        const currentStatus = (row?.status || "").toUpperCase().replace(/[-_ ]/g, "");
                        const isPending = currentStatus === "PENDING";
                        const isCheckedIn = currentStatus === "CHECKEDIN" || currentStatus === "PASSISSUED" || currentStatus === "APPROVED";

                        return (
                            <div key={row.visitorId || index} className="bg-white dark:bg-sectionheaderbg border border-gray-100 dark:border-bordercolor rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col">
                                {/* Main Card Content */}
                                <div className="py-3 sm:px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    {/* Left: Avatar & Info */}
                                    <div className="flex items-center gap-4">
                                        {row.image ? (
                                            <img src={row.image} className="w-10 h-10 rounded-full border border-gray-100 dark:border-bordercolor object-cover" alt={row.visitorName} />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-actionitembg border border-slate-200 dark:border-bordercolor flex items-center justify-center text-slate-400 dark:text-textsecondary">
                                                <FontAwesomeIcon icon={faUser} className="text-sm" />
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-0.5">
                                            <h4 className="font-bold text-[13px] text-[#1E293B] dark:text-textprimary">{row?.visitorName || "—"}</h4>
                                            <span className="text-[11px] font-semibold text-slate-500 dark:text-textsecondary">{row?.visitorMobileNumber || row?.mobile ? `+91 ${row.visitorMobileNumber || row.mobile}` : "—"}</span>
                                            <span className="text-[10px] text-slate-400 dark:text-textsecondary font-medium flex items-center gap-1 mt-0.5">
                                                <svg className="w-3.5 h-3.5 text-slate-400 dark:text-textsecondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                                </svg>
                                                Entry: {formatEntryTime(row?.createdDate || row?.requestedOn || row?.expectedEntryTime)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="flex flex-col items-end gap-3 self-end sm:self-center">
                                        {/* Status Label on Top */}

                                        <div className="flex items-center gap-3">
                                            {/* Status Badge */}
                                            <span className={`px-3 py-1 rounded-full text-[9px] font-black tracking-wider uppercase ${getStatusStyles(row?.status)}`}>
                                                {(row?.status || "PENDING").replaceAll("_", "-")}
                                            </span>

                                            {/* Buttons depending on status */}
                                            {isPending && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleUpdateStatus(row, 1)}
                                                        className="w-7 h-7 rounded-lg bg-[#E31F26] text-white flex items-center justify-center shadow-md hover:bg-red-700 transition-all active:scale-95"
                                                        title="Approve"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateStatus(row, 3)}
                                                        className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shadow-sm hover:bg-slate-200 transition-all active:scale-95"
                                                        title="Reject"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}

                                            {isCheckedIn && (
                                                <button
                                                    onClick={() => handleCheckOut(row?.visitorMobileNumber || row?.mobile)}
                                                    className="bg-[#E31F26] text-white font-black text-[10px] px-3.5 py-2 rounded-xl flex items-center gap-2 shadow-sm hover:bg-red-700 transition-all active:scale-95"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                                    </svg>
                                                    Check-Out
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom Details Grid */}
                                <div className="bg-[#F8FAFC] dark:bg-actionitembg border-t border-slate-100 dark:border-bordercolor px-4 py-2 sm:px-3 rounded-b-2xl">
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-xs">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Location</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px] truncate max-w-[150px]" title={row?.location || row?.coming_from}>{row?.location || row?.coming_from || "—"}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Joiners</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px]">{row?.additionalCount || '-'}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Purpose</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px] truncate max-w-[150px]" title={row?.purposeOfVisit || row?.purposeText}>{row?.purposeOfVisit || row?.purposeText || "—"}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Unit</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px]">{row?.unitName || "—"}</span>
                                        </div>
                                        {row?.slotName && row?.parkingStatus !== "Completed" && (
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Parking</span>
                                                <span className={`font-semibold text-[11px] ${row?.parkingStatus === "Held" ? "text-amber-600" : "text-blue-600"}`}>
                                                    {row.slotName} · {row?.parkingStatus === "Held" ? "Held (pending approval)" : row?.parkingStatus}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">VisitorType</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px] truncate max-w-[150px]">{row?.visitorType || "—"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                    }
                </div>
                {/* Custom Pagination Footer */}
                {pagination.totalRecords > 0 && (
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100 dark:border-bordercolor">
                        <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-[#64748B] dark:text-textsecondary">
                            <span>
                                Showing <span className="text-[#0F172A] dark:text-textprimary">{Math.min((pagination.pageNumber - 1) * pagination.pageSize + 1, pagination.totalRecords)}</span> to <span className="text-[#0F172A] dark:text-textprimary">{Math.min(pagination.pageNumber * pagination.pageSize, pagination.totalRecords)}</span> of <span className="text-[#0F172A] dark:text-textprimary">{pagination.totalRecords}</span> entries
                            </span>
                            <div className="flex items-center gap-1.5">
                                <span>Show</span>
                                <select
                                    value={pagination.pageSize}
                                    onChange={(e) => {
                                        const newSize = Number(e.target.value);
                                        setPagination(prev => ({ ...prev, pageSize: newSize, pageNumber: 1 }));
                                    }}
                                    className="border border-gray-200 dark:border-bordercolor rounded-lg px-2 py-1 bg-white dark:bg-actionitembg font-bold text-[#475569] dark:text-textprimary outline-none text-xs"
                                >
                                    {[10, 25, 50, 100].map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                                <span>entries</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                disabled={pagination.pageNumber === 1}
                                onClick={() => setPagination(prev => ({ ...prev, pageNumber: Math.max(1, prev.pageNumber - 1) }))}
                                className={`flex items-center justify-center px-4 h-8 rounded-lg border text-xs font-bold transition-all duration-200
                                  ${pagination.pageNumber === 1
                                        ? "bg-gray-50 dark:bg-actionitembg text-gray-300 dark:text-gray-600 border-gray-100 dark:border-bordercolor cursor-not-allowed"
                                        : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border-gray-200 dark:border-bordercolor hover:border-red-500 hover:text-red-500"
                                    }`}
                            >
                                Back
                            </button>
                            <div className="flex items-center gap-1">
                                {(() => {
                                    const totalPages = Math.ceil(pagination.totalRecords / pagination.pageSize) || 1;
                                    const currentPage = pagination.pageNumber;
                                    let pages = [];
                                    if (totalPages <= 7) {
                                        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                                    } else if (currentPage <= 4) {
                                        pages = [1, 2, 3, 4, 5, '...', totalPages];
                                    } else if (currentPage >= totalPages - 3) {
                                        pages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                                    } else {
                                        pages = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
                                    }
                                    return pages.map((page, index) => {
                                        if (page === '...') {
                                            return <span key={`ellipsis-${index}`} className="px-2 text-gray-400">...</span>;
                                        }
                                        const isCurrent = currentPage === page;
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setPagination(prev => ({ ...prev, pageNumber: page }))}
                                                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all duration-200
                                                ${isCurrent
                                                        ? "text-white"
                                                        : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border border-gray-200 dark:border-bordercolor hover:border-red-500 hover:text-red-500"
                                                    }`}
                                                style={isCurrent ? { backgroundColor: primaryColor(), borderColor: primaryColor() } : {}}
                                            >
                                                {page}
                                            </button>
                                        );
                                    });
                                })()}
                            </div>
                            <button
                                disabled={pagination.pageNumber === (Math.ceil(pagination.totalRecords / pagination.pageSize) || 1)}
                                onClick={() => setPagination(prev => ({ ...prev, pageNumber: Math.min(Math.ceil(prev.totalRecords / prev.pageSize) || 1, prev.pageNumber + 1) }))}
                                className={`flex items-center justify-center px-4 h-8 rounded-lg border text-xs font-bold transition-all duration-200
                                  ${pagination.pageNumber === (Math.ceil(pagination.totalRecords / pagination.pageSize) || 1)
                                        ? "bg-gray-50 dark:bg-actionitembg text-gray-300 dark:text-gray-600 border-gray-100 dark:border-bordercolor cursor-not-allowed"
                                        : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border-gray-200 dark:border-bordercolor hover:border-red-500 hover:text-red-500"
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WalkInVisitorList;
